import React from "react";
import {
	makeStyles,
	tokens,
	Link,
	MessageBar,
	MessageBarBody,
} from "@fluentui/react-components";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/BaseAuthProvider";

const useStyles = makeStyles({
	banner: {
		marginTop: tokens.spacingVerticalM,
		marginInline: "0.75rem",
	},
	bannerContent: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
		gap: tokens.spacingHorizontalS,
	},
	subscribeLink: {
		fontWeight: tokens.fontWeightSemibold,
		whiteSpace: "nowrap",
	},
});

interface TrialBannerProps {
	onSubscribeClick?: () => void;
}

/**
 * TrialBanner component - shows trial countdown during active trial.
 * Hidden when not authenticated or still loading.
 */
export const TrialBanner: React.FC<TrialBannerProps> = ({
	onSubscribeClick,
}) => {
	const styles = useStyles();
	const { isAuthenticated } = useAuth();
	const {
		isTrialActive,
		trialDaysRemaining,
		subscriptionStatus,
		loading,
		checkoutInProgress,
	} = useSubscription();

	if (loading || !isAuthenticated) {
		return null;
	}

	// Active subscription — nothing to prompt.
	if (subscriptionStatus === "active") {
		return null;
	}

	const handleSubscribeClick = (
		e: React.MouseEvent<HTMLAnchorElement>,
	) => {
		e.preventDefault();
		if (onSubscribeClick) {
			onSubscribeClick();
		}
	};

	// Active trial → countdown. Otherwise the trial has lapsed (expired /
	// cancelled / past due) and the user must subscribe to keep inserting.
	const message = isTrialActive
		? `Trial: ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? "s" : ""} remaining`
		: "Your trial has ended — subscribe to keep inserting";

	return (
		<MessageBar className={styles.banner} intent="warning">
			<MessageBarBody>
				<div className={styles.bannerContent}>
					<span>{message}</span>
					<Link
						className={styles.subscribeLink}
						onClick={handleSubscribeClick}
						href="#"
						disabled={checkoutInProgress}
					>
						{checkoutInProgress
							? "Opening\u2026"
							: "Subscribe"}
					</Link>
				</div>
			</MessageBarBody>
		</MessageBar>
	);
};

export default TrialBanner;
